import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { 
  Bus, ArrowLeft, Plus, Edit, Trash2, CheckCircle, 
  AlertCircle, Settings, X, Save
} from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import api from '../../utils/api';

const BusOperatorFleet = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [buses, setBuses] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingBus, setEditingBus] = useState(null);
  const [formData, setFormData] = useState({
    registration_number: '',
    make: '',
    model: '',
    year: new Date().getFullYear(),
    capacity: 48,
    amenities: [],
    images: []
  });

  const amenityOptions = ['WiFi', 'AC', 'TV', 'USB Charging', 'Reclining Seats', 'Refreshments', 'Blankets', 'Toilet'];

  useEffect(() => {
    // Check if user is logged in before fetching
    const token = localStorage.getItem('tekstar_token');
    if (!token) {
      navigate('/bus/operator/login');
      return;
    }
    fetchFleet();
  }, [navigate]);

  const fetchFleet = async () => {
    try {
      setLoading(true);
      const response = await api.get('/bus/operators/fleet');
      setBuses(response.data.buses || []);
    } catch (error) {
      if (error.response?.status === 401) {
        // Token expired or invalid
        localStorage.removeItem('tekstar_token');
        localStorage.removeItem('tekstar_user');
        navigate('/bus/operator/login');
      } else if (error.response?.status === 404) {
        // No operator profile - redirect to apply
        navigate('/bus/operator/apply');
      } else {
        console.error('Failed to load fleet:', error);
        toast({
          title: 'Error',
          description: error.response?.data?.detail || 'Failed to load fleet',
          variant: 'destructive'
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingBus) {
        await api.put(`/bus/operators/fleet/${editingBus.id}`, formData);
        toast({ title: 'Success', description: 'Bus updated successfully' });
      } else {
        await api.post('/bus/operators/fleet', formData);
        toast({ title: 'Success', description: 'Bus added successfully' });
      }
      
      setShowForm(false);
      setEditingBus(null);
      resetForm();
      fetchFleet();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to save bus',
        variant: 'destructive'
      });
    }
  };

  const handleEdit = (bus) => {
    setEditingBus(bus);
    setFormData({
      registration_number: bus.registrationNumber,
      make: bus.make,
      model: bus.model,
      year: bus.year,
      capacity: bus.capacity,
      amenities: bus.amenities || [],
      images: bus.images || []
    });
    setShowForm(true);
  };

  const handleDelete = async (busId) => {
    if (!window.confirm('Are you sure you want to delete this bus?')) return;
    
    try {
      await api.delete(`/bus/operators/fleet/${busId}`);
      toast({ title: 'Success', description: 'Bus deleted' });
      fetchFleet();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to delete bus',
        variant: 'destructive'
      });
    }
  };

  const toggleAmenity = (amenity) => {
    setFormData(prev => ({
      ...prev,
      amenities: prev.amenities.includes(amenity)
        ? prev.amenities.filter(a => a !== amenity)
        : [...prev.amenities, amenity]
    }));
  };

  const resetForm = () => {
    setFormData({
      registration_number: '',
      make: '',
      model: '',
      year: new Date().getFullYear(),
      capacity: 48,
      amenities: [],
      images: []
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-700';
      case 'maintenance': return 'bg-yellow-100 text-yellow-700';
      case 'retired': return 'bg-gray-100 text-gray-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 pb-20">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white p-4">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Link to="/bus/operator">
                <ArrowLeft className="h-6 w-6" />
              </Link>
              <div>
                <h1 className="text-xl font-bold">Fleet Management</h1>
                <p className="text-indigo-200 text-sm">{buses.length} bus(es)</p>
              </div>
            </div>
            <Button 
              onClick={() => { setShowForm(true); setEditingBus(null); resetForm(); }}
              className="bg-white text-indigo-600 hover:bg-indigo-50"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Bus
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto p-4">
        {/* Add/Edit Form Modal */}
        {showForm && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <Card className="w-full max-w-lg max-h-[90vh] overflow-y-auto p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold">
                  {editingBus ? 'Edit Bus' : 'Add New Bus'}
                </h2>
                <button onClick={() => { setShowForm(false); setEditingBus(null); }}>
                  <X className="h-6 w-6 text-gray-400" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label>Registration Number *</Label>
                  <Input
                    value={formData.registration_number}
                    onChange={(e) => setFormData({ ...formData, registration_number: e.target.value })}
                    placeholder="e.g., BAA 1234 ZM"
                    required
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Make *</Label>
                    <Input
                      value={formData.make}
                      onChange={(e) => setFormData({ ...formData, make: e.target.value })}
                      placeholder="e.g., Scania"
                      required
                    />
                  </div>
                  <div>
                    <Label>Model *</Label>
                    <Input
                      value={formData.model}
                      onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                      placeholder="e.g., Touring HD"
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Year *</Label>
                    <Input
                      type="number"
                      value={formData.year}
                      onChange={(e) => setFormData({ ...formData, year: parseInt(e.target.value) })}
                      min={2000}
                      max={new Date().getFullYear() + 1}
                      required
                    />
                  </div>
                  <div>
                    <Label>Capacity (seats) *</Label>
                    <Input
                      type="number"
                      value={formData.capacity}
                      onChange={(e) => setFormData({ ...formData, capacity: parseInt(e.target.value) })}
                      min={10}
                      max={80}
                      required
                    />
                  </div>
                </div>

                <div>
                  <Label>Amenities</Label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {amenityOptions.map((amenity) => (
                      <button
                        key={amenity}
                        type="button"
                        onClick={() => toggleAmenity(amenity)}
                        className={`px-3 py-1 rounded-full text-sm border transition-all ${
                          formData.amenities.includes(amenity)
                            ? 'bg-indigo-100 border-indigo-500 text-indigo-700'
                            : 'bg-gray-50 border-gray-200 text-gray-600 hover:border-indigo-300'
                        }`}
                      >
                        {formData.amenities.includes(amenity) && (
                          <CheckCircle className="h-3 w-3 inline mr-1" />
                        )}
                        {amenity}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="flex gap-3 pt-4">
                  <Button 
                    type="button" 
                    variant="outline" 
                    className="flex-1"
                    onClick={() => { setShowForm(false); setEditingBus(null); }}
                  >
                    Cancel
                  </Button>
                  <Button type="submit" className="flex-1 bg-indigo-600 hover:bg-indigo-700">
                    <Save className="h-4 w-4 mr-2" />
                    {editingBus ? 'Update Bus' : 'Add Bus'}
                  </Button>
                </div>
              </form>
            </Card>
          </div>
        )}

        {/* Fleet List */}
        {buses.length === 0 ? (
          <Card className="p-8 text-center">
            <Bus className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-600 mb-2">No Buses Yet</h3>
            <p className="text-gray-500 mb-4">Add your first bus to start managing your fleet</p>
            <Button 
              onClick={() => setShowForm(true)}
              className="bg-indigo-600 hover:bg-indigo-700"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Your First Bus
            </Button>
          </Card>
        ) : (
          <div className="space-y-4">
            {buses.map((bus) => (
              <Card key={bus.id} className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-4">
                    <div className="p-3 bg-indigo-100 rounded-lg">
                      <Bus className="h-8 w-8 text-indigo-600" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold text-lg">{bus.registrationNumber}</h3>
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${getStatusColor(bus.status)}`}>
                          {bus.status}
                        </span>
                      </div>
                      <p className="text-gray-600">{bus.make} {bus.model} ({bus.year})</p>
                      <p className="text-sm text-gray-500">{bus.capacity} seats</p>
                      
                      {bus.amenities?.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {bus.amenities.map((amenity, idx) => (
                            <span key={idx} className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded">
                              {amenity}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => handleEdit(bus)}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button 
                      size="sm" 
                      variant="outline"
                      className="text-red-600 hover:bg-red-50"
                      onClick={() => handleDelete(bus.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default BusOperatorFleet;
